import { defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, button, container, loader } from '@/ui/components';
import { filterListToOptions, IdProps, sequence, toastAndRedirect } from '@/ui/helpers/uiHelper';
import { useTask } from '@/ui/concerns';
import { fromEditable, get, intoEditable, update } from '@/services/api/resources/user';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { filter as getAllGroups } from '@/services/api/resources/group';
import { filter as getAllRoles } from '@/services/api/resources/role';
import { filter as getAllEntityFolders } from '@/services/api/resources/entityFolder';
import { versioned } from '@/services/api';
import { hasPermission, Permissions } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { useUserRecord } from './useUserRecord';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { useUserEditForm } from '@/ui/pages/user/useUserEditForm';
import { AdministrationTabNames, queryTabName } from '@/ui/components/navigation/constants';

export const UserEditGeneralTab = defineComponent<IdProps>({
  name: 'UserEditGeneralTab',
  props: ['id'],
  setup(props) {
    if (!hasPermission(Permissions.MANAGE_USERS)) {
      return renderForbiddenPage;
    }

    let recordVersion = '';
    const entityItemOptions = ref<SelectOptions>([]);
    const roleOptions = ref<SelectOptions>([]);
    const groupOptions = ref<SelectOptions>([]);
    const entityFolderOptions = ref<SelectOptions>([]);

    const record = useUserRecord();

    const getRecord = useTask(
      () => sequence(get(props.id), getAllEntityItems(), getAllGroups(), getAllRoles(), getAllEntityFolders()),
      ([{ data, version }, entityItems, groups, roles, entityFolders]) => {
        entityItemOptions.value = filterListToOptions(entityItems);
        groupOptions.value = filterListToOptions(groups);
        roleOptions.value = filterListToOptions(roles);
        entityFolderOptions.value = filterListToOptions(entityFolders);
        record.value = intoEditable(data);
        recordVersion = version;
      },
    );

    watch(ref(props.id), getRecord.run);

    const sendRecord = useTask(
      () => update(props.id, versioned(fromEditable(record.value), recordVersion)),
      () => toastAndRedirect(t('user.updated'), 'administration', { [queryTabName]: AdministrationTabNames.USER }),
    );

    const { formFields, handleSubmit } = useUserEditForm(record, sendRecord);

    return function render() {
      return container(
        [
          box([loader('form', getRecord, [
            ...formFields(entityItemOptions.value, groupOptions.value, roleOptions.value, entityFolderOptions.value),
            button(
              t('user.update'),
              handleSubmit,
              undefined,
              { loading: sendRecord.state.value.pending },
            ),
          ])]),
        ],
        'narrow',
      );
    };
  },
});
